import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  type SupportContact,
  getContactWhatsappTarget,
  getDefaultSupportContact as getDefaultSupportContactFromList,
  parseSupportContacts,
} from '../support-contacts';

// Carousel slide type for ssliquor.co.ke-style hero carousel
export interface CarouselSlide {
  id: string;
  url: string;
  alt: string;
  link?: string;
  title?: string;
  subtitle?: string;
}

export interface StoreSettings {
  business_name: string;
  support_phone: string;
  whatsapp_business_number: string;
  support_numbers: SupportContact[];
  delivery_fee: string;
  free_delivery_threshold: string;
  show_prices: string; // 'true' or 'false' - global price visibility
  vat_rate: string; // VAT rate as string (e.g., '0.16' for 16%)
  
  // Banner settings - NO default fallback to local assets
  banner_enabled: boolean;
  banner_url: string;       // Empty string = no banner configured
  banner_alt: string;
  banner_fit: 'cover' | 'contain' | 'fill' | 'scale-down';
  banner_layout: 'compact' | 'balanced' | 'cinematic';
  
  // Logo settings - for header branding
  logo_url: string;         // Empty string = no custom logo
  logo_alt: string;
  
  offers_timezone: string; // IANA timezone for offer scheduling (e.g. "Africa/Nairobi")
  promotions: string; // JSON string of promotions array
  testimonials: string; // JSON string of testimonials array
  // Carousel settings for ssliquor.co.ke-style hero section
  carousel_enabled: boolean;
  carousel_autoplay: boolean;
  carousel_interval: number;
  carousel_images: CarouselSlide[];
  // Jumbo/hero image settings
  jumbo_image_url: string;
  jumbo_image_enabled: boolean;
  // Messaging templates
  whatsapp_order_message_template: string;
  // Social media
  social_facebook: string;
  social_instagram: string;
  social_twitter: string;
  social_tiktok: string;
  social_show_share_buttons: boolean;
  // SMS per-event enables
  sms_event_order_confirmed_enabled: boolean;
  sms_event_new_order_admin_enabled: boolean;
  sms_event_rider_assigned_rider_enabled: boolean;
  sms_event_rider_assigned_customer_enabled: boolean;
  // Stock management
  unlimited_stock: boolean; // When true, stock checks are bypassed — all items are always orderable
}

const defaultSettings: StoreSettings = {
  business_name: 'Karebe Wines & Spirits',
  support_phone: '',
  whatsapp_business_number: '',
  support_numbers: [],
  delivery_fee: '300',
  free_delivery_threshold: '5000',
  show_prices: 'true',
  vat_rate: '0',
  
  // Banner defaults to EMPTY - no fallback to local assets
  banner_enabled: true,
  banner_url: '',           // ← CRITICAL: Empty = hidden, no fallback
  banner_alt: '',
  banner_fit: 'cover',
  banner_layout: 'balanced',
  
  // Logo defaults to EMPTY - no fallback
  logo_url: '',             // ← CRITICAL: Empty = hidden, no fallback  
  logo_alt: 'Karebe Wines & Spirits',
  offers_timezone: 'Africa/Nairobi',
  promotions: JSON.stringify([
    {
      id: '1',
      title: 'Weekend Special',
      description: 'Get 15% off all wines this weekend!',
      discount: '15%',
      code: 'WINE15',
      expiresAt: '2025-03-31',
      bgColor: 'from-amber-500 to-orange-600',
    },
    {
      id: '2',
      title: 'Beer Bundle Deal',
      description: 'Buy 2 cases of Tusker and get 1 free!',
      discount: '33%',
      code: 'TUSKER3',
      expiresAt: '2025-03-25',
      bgColor: 'from-green-500 to-emerald-600',
    },
    {
      id: '3',
      title: 'Whiskey Wednesday',
      description: 'Every Wednesday, get 10% off all whiskey',
      discount: '10%',
      code: 'WHISKEY10',
      expiresAt: '2025-12-31',
      bgColor: 'from-amber-700 to-yellow-600',
    },
  ]),
  testimonials: JSON.stringify([
    {
      id: '1',
      name: 'James M.',
      rating: 5,
      text: 'Fast delivery and great prices. The whiskey selection is amazing!',
      date: '2025-03-10',
    },
    {
      id: '2',
      name: 'Sarah K.',
      rating: 5,
      text: 'Best online liquor store in Kenya. Will definitely order again!',
      date: '2025-03-08',
    },
    {
      id: '3',
      name: 'Michael O.',
      rating: 4,
      text: 'Quality products and excellent customer service. Highly recommended.',
      date: '2025-03-05',
    },
  ]),
  // Carousel settings for ssliquor.co.ke-style hero section
  carousel_enabled: false,
  carousel_autoplay: true,
  carousel_interval: 5000,
  carousel_images: [],
  // Jumbo/hero image settings
  jumbo_image_url: '',
  jumbo_image_enabled: false,
  // Messaging templates
  whatsapp_order_message_template: 'Hello Karebe, I just created order {{order_reference}} for {{items_summary}}{{amount_summary}}. Please confirm the next step.',
  // Social media
  social_facebook: '',
  social_instagram: '',
  social_twitter: '',
  social_tiktok: '',
  social_show_share_buttons: true,
  // SMS per-event enables
  sms_event_order_confirmed_enabled: true,
  sms_event_new_order_admin_enabled: true,
  sms_event_rider_assigned_rider_enabled: true,
  sms_event_rider_assigned_customer_enabled: true,
  // Stock management — default ON (unlimited) so all products are orderable out of the box
  unlimited_stock: true,
};

let cachedSettings: StoreSettings = { ...defaultSettings };
let settingsListeners: Array<(settings: StoreSettings) => void> = [];
const DEMO_SETTINGS_KEY = 'karebe_demo_settings';
let hasLoadedSettings = false;

function notifySettingsUpdate(next: StoreSettings) {
  cachedSettings = next;
  hasLoadedSettings = true;
  settingsListeners.forEach(listener => listener(cachedSettings));
}

/**
 * Global settings store - singleton pattern
 * Loads settings from Supabase and provides to all components
 */
export function useSettings() {
  const [settings, setSettings] = useState<StoreSettings>(cachedSettings);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Add listener for updates
    settingsListeners.push(setSettings);

    if (!hasLoadedSettings) {
      loadSettings();
    } else {
      setIsLoading(false);
    }

    return () => {
      settingsListeners = settingsListeners.filter(l => l !== setSettings);
    };
  }, []);

  return { settings, isLoading };
}

/**
 * Load settings from Supabase
 */
export async function loadSettings(): Promise<StoreSettings> {
  try {
    if (!supabase) {
      const raw = localStorage.getItem(DEMO_SETTINGS_KEY);
      const parsed = raw ? (JSON.parse(raw) as Partial<StoreSettings>) : {};
      const supportNumbers = Array.isArray(parsed.support_numbers)
        ? parsed.support_numbers
        : parseSupportContacts(undefined, {
            supportPhone: parsed.support_phone,
            whatsappBusinessNumber: parsed.whatsapp_business_number,
          });
      const defaultSupportContact = getDefaultSupportContactFromList(supportNumbers);
      const merged = {
        ...defaultSettings,
        ...parsed,
        support_phone: defaultSupportContact?.phone || '',
        whatsapp_business_number: getContactWhatsappTarget(defaultSupportContact),
        support_numbers: supportNumbers,
      };
      notifySettingsUpdate(merged);
      console.log('[Settings] Demo mode - using local settings');
      return merged;
    }

    const result = await supabase
      .from('admin_settings')
      .select('setting_key, setting_value');
    
    const data = result.data;
    const error = result.error;

    if (error) {
      console.log('[Settings] Error loading settings:', error.message);
      return defaultSettings;
    }

    const settingsMap: Record<string, string> = {};
    data?.forEach((row) => {
      settingsMap[row.setting_key] = row.setting_value || '';
    });

    const supportNumbers = parseSupportContacts(settingsMap.support_contact_numbers, {
      supportPhone: settingsMap.support_phone,
      whatsappBusinessNumber: settingsMap.whatsapp_business_number,
    });
    const defaultSupportContact = getDefaultSupportContactFromList(supportNumbers);

    // IMPORTANT: No fallback to local banner/logo images
    // If banner_url or logo_url is not set in DB, they remain empty
    const nextSettings: StoreSettings = {
      business_name: settingsMap.business_name || defaultSettings.business_name,
      support_phone: defaultSupportContact?.phone || '',
      whatsapp_business_number: getContactWhatsappTarget(defaultSupportContact),
      support_numbers: supportNumbers,
      delivery_fee: settingsMap.delivery_fee || defaultSettings.delivery_fee,
      free_delivery_threshold: settingsMap.free_delivery_threshold || defaultSettings.free_delivery_threshold,
      show_prices: settingsMap.show_prices || defaultSettings.show_prices,
      vat_rate: settingsMap.vat_rate || defaultSettings.vat_rate,
      
      // Banner: ONLY use DB value, no fallback to local assets
      banner_enabled: settingsMap.banner_enabled !== 'false',
      banner_url: settingsMap.banner_url || '',  // ← NO FALLBACK
      banner_alt: settingsMap.banner_alt || defaultSettings.banner_alt,
      banner_fit: (settingsMap.banner_fit as StoreSettings['banner_fit']) || defaultSettings.banner_fit,
      banner_layout: (settingsMap.banner_layout as StoreSettings['banner_layout']) || defaultSettings.banner_layout,
      
      // Logo: ONLY use DB value, no fallback to local assets  
      logo_url: settingsMap.logo_url || '',       // ← NO FALLBACK
      logo_alt: settingsMap.logo_alt || defaultSettings.logo_alt,
      
      offers_timezone: settingsMap.offers_timezone || defaultSettings.offers_timezone,
      promotions: settingsMap.promotions || defaultSettings.promotions,
      testimonials: settingsMap.testimonials || defaultSettings.testimonials,
      
      // Carousel settings for ssliquor.co.ke-style hero section
      carousel_enabled: settingsMap.carousel_enabled === 'true',
      carousel_autoplay: settingsMap.carousel_autoplay !== 'false',
      carousel_interval: parseInt(settingsMap.carousel_interval || '5000', 10),
      carousel_images: settingsMap.carousel_images ? JSON.parse(settingsMap.carousel_images) : defaultSettings.carousel_images,
      
      // Jumbo/hero image settings
      jumbo_image_url: settingsMap.jumbo_image_url || '',
      jumbo_image_enabled: settingsMap.jumbo_image_enabled === 'true',
      // Messaging templates
      whatsapp_order_message_template: settingsMap.whatsapp_order_message_template || defaultSettings.whatsapp_order_message_template,
      // Social media
      social_facebook: settingsMap.social_facebook || '',
      social_instagram: settingsMap.social_instagram || '',
      social_twitter: settingsMap.social_twitter || '',
      social_tiktok: settingsMap.social_tiktok || '',
      social_show_share_buttons: settingsMap.social_show_share_buttons !== 'false',
      // SMS per-event enables
      sms_event_order_confirmed_enabled: settingsMap.sms_event_order_confirmed_enabled !== 'false',
      sms_event_new_order_admin_enabled: settingsMap.sms_event_new_order_admin_enabled !== 'false',
      sms_event_rider_assigned_rider_enabled: settingsMap.sms_event_rider_assigned_rider_enabled !== 'false',
      sms_event_rider_assigned_customer_enabled: settingsMap.sms_event_rider_assigned_customer_enabled !== 'false',
      // Stock management — default true if key not set yet in DB
      unlimited_stock: settingsMap.unlimited_stock !== 'false',
    };
    notifySettingsUpdate(nextSettings);

    console.log('[Settings] Loaded from Supabase:', nextSettings);
    return nextSettings;
  } catch (error) {
    console.error('[Settings] Failed to load settings:', error);
    notifySettingsUpdate(defaultSettings);
    return defaultSettings;
  }
}

/**
 * Merge new settings into cache and notify listeners
 */
export function mergeSettingsCache(partial: Partial<StoreSettings>) {
  const next = {
    ...cachedSettings,
    ...partial,
    support_numbers: partial.support_numbers ?? cachedSettings.support_numbers,
  };
  notifySettingsUpdate(next);
  if (!supabase) {
    localStorage.setItem(DEMO_SETTINGS_KEY, JSON.stringify(next));
  }
}

/**
 * Get support phone number - used throughout the app
 */
export function getSupportPhone(): string {
  return getDefaultSupportContact()?.phone || '';
}

/**
 * Get WhatsApp business number - used throughout the app
 */
export function getWhatsAppNumber(): string {
  return getContactWhatsappTarget(getDefaultSupportContact());
}

export function getSupportContacts(): SupportContact[] {
  return cachedSettings.support_numbers;
}

export function getDefaultSupportContact(): SupportContact | null {
  return getDefaultSupportContactFromList(cachedSettings.support_numbers);
}

/**
 * Check if prices should be shown globally
 */
export function getShowPrices(): boolean {
  return cachedSettings.show_prices !== 'false';
}

/**
 * Hook to check if prices should be shown
 */
export function useShowPrices() {
  const { settings } = useSettings();
  return settings.show_prices !== 'false';
}

/**
 * Check if unlimited stock mode is active (sync, from cache)
 */
export function getUnlimitedStock(): boolean {
  return cachedSettings.unlimited_stock !== false;
}

/**
 * Hook to check if unlimited stock mode is active.
 * When true, all stock checks are bypassed — customers can always add items.
 */
export function useUnlimitedStock(): boolean {
  const { settings } = useSettings();
  return settings.unlimited_stock !== false;
}

/**
 * Get delivery fee from settings
 */
export function getDeliveryFee(): number {
  const fee = parseInt(cachedSettings.delivery_fee || '300', 10);
  return isNaN(fee) ? 300 : fee;
}

/**
 * Get free delivery threshold from settings
 */
export function getFreeDeliveryThreshold(): number {
  const threshold = parseInt(cachedSettings.free_delivery_threshold || '5000', 10);
  return isNaN(threshold) ? 5000 : threshold;
}

/**
 * Get VAT rate from settings
 */
export function getVatRate(): number {
  const rate = parseFloat(cachedSettings.vat_rate || '0');
  return isNaN(rate) ? 0 : rate;
}

/**
 * Check if a banner is configured (non-empty)
 */
export function hasBannerConfigured(): boolean {
  return Boolean(
    cachedSettings.banner_enabled &&
    cachedSettings.banner_url &&
    cachedSettings.banner_url.length > 0
  );
}

/**
 * Check if a logo is configured (non-empty)
 */
export function hasLogoConfigured(): boolean {
  return Boolean(cachedSettings.logo_url && cachedSettings.logo_url.length > 0);
}
